import { useEffect, useMemo, useState } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiFilter,
  FiTag,
  FiArrowUpCircle,
  FiArrowDownCircle,
  FiUpload,
  FiDownload,
  FiCornerDownRight,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  CategoryForm,
  Select,
  MultiSelect,
  CategoryImportModal,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import { usePermission } from '../../hooks/usePermission';

import '../CompanyManager/style.css';

export default function CategoryManager() {
  const permissions = usePermission('financial');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadCategories(selectedCompanyFilter || 'all');
  }, [selectedCompanyFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter, selectedCompanyFilter]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Erro ao carregar empresas.');
    }
  }

  async function loadCategories(companyId) {
    try {
      setLoading(true);
      const data = await Api.getCategories(companyId);

      if (Array.isArray(data)) {
        setCategories(data);
        setHasLoadedOnce(true);
      } else {
        setError('Não foi possível carregar as categorias.');
      }
    } catch (err) {
      setError('Erro ao carregar categorias. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleExport() {
    if (!categories || categories.length === 0) {
      toast.warning('Não há dados para exportar.');
      return;
    }

    // Create a map for parent name lookup
    const categoryMap = new Map();
    categories.forEach(c => categoryMap.set(c.id, c.name));

    const exportData = categories.map(c => ({
      'Nome': c.name,
      'Empresa': c.company?.trade_name || c.company?.corporate_name || '',
      'Tipo': c.type,
      'Categoria Pai': c.parent_id ? categoryMap.get(c.parent_id) || '' : '',
      'Descrição': c.description || '',
      'Status': c.active ? 'Ativo' : 'Inativo'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Categorias");
    XLSX.writeFile(wb, "categorias.xlsx");
  }

  function handleCreate() {
    if (companies.length === 0) loadCompanies();
    setSelectedCategory(null);
    setIsModalOpen(true);
  }

  function handleEdit(category) {
    if (companies.length === 0) loadCompanies();
    setSelectedCategory(category);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadCategories(selectedCompanyFilter || 'all');
    setIsModalOpen(false);
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyFilter(idsWithoutAll[0]);
    } else {
      setSelectedCompanyFilter('');
    }
  }

  function handleDeleteClick(category) {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!categoryToDelete) return;

    try {
      setIsDeleting(true);
      await Api.deleteCategory(categoryToDelete.company_id, categoryToDelete.id);
      loadCategories(selectedCompanyFilter || 'all');
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir categoria.');
    } finally {
      setIsDeleting(false);
    }
  }

  // Helper to build hierarchy
  const hierarchyData = useMemo(() => {
    if (!categories || categories.length === 0) return [];

    // Group by parent_id
    const groups = {};
    categories.forEach(c => {
      const pId = c.parent_id || 'root';
      if (!groups[pId]) groups[pId] = [];
      groups[pId].push(c);
    });

    // Recursive flatten
    const result = [];
    
    function traverse(parentId, depth) {
      const children = groups[parentId];
      if (!children) return;
      
      // Sort children by name
      children.sort((a, b) => a.name.localeCompare(b.name));

      children.forEach(c => {
        result.push({ ...c, depth });
        traverse(c.id, depth + 1);
      });
    }

    traverse('root', 0);
    return result;
  }, [categories]);


  const filteredCategories = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);
    
    return hierarchyData.filter(category => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        category.name?.toLowerCase().includes(searchLower) ||
        category.description?.toLowerCase().includes(searchLower);
      
      const matchesType = typeFilter === 'all' || category.type === typeFilter;

      const companyId = category.company_id || category.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesType && matchesCompany;
    });
  }, [hierarchyData, searchTerm, typeFilter, companyFilterValues]);

  const totalItems = filteredCategories.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedCategories = filteredCategories.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'name',
      header: 'Nome',
      width: '2fr',
      cellClassName: 'manager-table-cell-primary',
      render: category => (
        <div style={{ paddingLeft: category.depth ? `${category.depth * 24}px` : 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {category.depth > 0 && <FiCornerDownRight size={14} style={{ marginRight: 8, color: '#9ca3af' }} />}
          <div className="manager-table-avatar">
            <FiTag size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {category.name}
            </span>
            {category.parent && (
                 <span className="manager-table-subtitle" style={{ fontSize: '0.75rem' }}>
                    {category.parent.name}
                 </span>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Tipo',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: category => (
        <span className={`manager-tag ${category.type === 'Receita' ? 'manager-tag-success' : 'manager-tag-danger'}`}>
          {category.type === 'Receita' ? (
            <FiArrowUpCircle size={14} style={{ marginRight: 6 }} />
          ) : (
            <FiArrowDownCircle size={14} style={{ marginRight: 6 }} />
          )}
          {category.type}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Descrição',
      width: '3fr',
      cellClassName: 'manager-table-cell',
      render: category => (
        <span className="manager-table-subtitle">
          {category.description || '-'}
        </span>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: category => (
        <span className="manager-table-subtitle">
          {category.company?.trade_name || category.company?.corporate_name || '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: category => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(category);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(category);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function CategorySkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const isMobile = window.innerWidth <= 768;

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiTag size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <CategorySkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiTag size={22} />
                </div>
                <div>
                  <h1>Categorias</h1>
                  <p>
                    Visualize e gerencie as categorias financeiras da empresa selecionada.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="secondary"
                  className="manager-header-button"
                  onClick={handleExport}
                >
                  <FiDownload size={18} />
                  {!isMobile && 'Exportar'}
                </Button>
                
                {permissions.canCreate && (
                  <Button
                    variant="secondary"
                    className="manager-header-button"
                    onClick={() => {
                      if (companies.length === 0) loadCompanies();
                      setIsImportModalOpen(true);
                    }}
                  >
                    <FiUpload size={18} />
                    {!isMobile && 'Importar'}
                  </Button>
                )}

                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Categoria
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {categories.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredCategories.length} de {categories.length} registros
                  </span>
                )}
              </div>

              <div className='manager-filters-grid-custom'>
                <div style={{ flex: 1 }}>
                  <MultiSelect
                    id="companyFilter"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({ value: String(c.id), label: c.trade_name || c.corporate_name }))
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                    placeholder="Filtrar por empresa"
                    label={"Empresa"}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    id="search"
                    label="Buscar"
                    placeholder="Buscar por nome..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    icon={FiSearch}
                  />
                </div>
                <Select
                  id="typeFilter"
                  label="Tipo"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'Todos os Tipos' },
                    { value: 'Receita', label: 'Receita' },
                    { value: 'Despesa', label: 'Despesa' },
                  ]}
                  icon={FiTag}
                  searchable
                />
              </div>
            </Card>

            {error ? (
              <div className="manager-error-container">
                <p>{error}</p>
                <Button onClick={() => loadCategories(selectedCompanyFilter || 'all')}>
                  Tentar Novamente
                </Button>
              </div>
            ) : (
              <Table
                items={paginatedCategories}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                getRowKey={(category) => category.id}
                emptyMessage="Nenhuma categoria encontrada."
              />
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <Modal
          title={selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <CategoryForm
            initialData={selectedCategory}
            companies={companies}
            categories={categories}
            companyId={selectedCompanyFilter}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalOpen(false)}
          />
        </Modal>
      )}

      {isDeleteModalOpen && (
        <Modal
          title="Confirmar Exclusão"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja excluir a categoria{' '}
              <span className="delete-modal-company-name">
                {categoryToDelete?.name}
              </span>
              ?
            </p>
            <p>Essa ação não poderá ser desfeita.</p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>
      )}

      {isImportModalOpen && (
        <CategoryImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => loadCategories(selectedCompanyFilter || 'all')}
        companyId={selectedCompanyFilter}
        companies={companies}
      />
      )}
    </PageComponent>
  );
}
